/**
 * Product analytics — Google Analytics for Firebase, through the Firebase JS SDK.
 *
 * WHAT IS MEASURED (and nothing more):
 *  - screen views — one `screen_view` per route the user lands on, keyed by the
 *    route NAME (`conversation`) and the matched route PATTERN
 *    (`/tabs/messages/:id`) — never a concrete URL;
 *  - sign-in (`login`, with its method) and sign-out (`sign_out`);
 *  - whatever a screen reports explicitly through {@link trackEvent}.
 *
 * PRIVACY CONTRACT (docs/analytics.md; the in-app Privacy Policy lists
 * analytics under "Device & usage" and service providers):
 *  - GA's `user_id` is a one-way SHA-256 of `syntry:<company>:<userId>` — a
 *    stable pseudonym, never the ERPat employee id, name or email.
 *  - Every event we send overrides gtag's automatic `page_location` (the live
 *    `location.href`) with the route PATTERN, so a record id in the URL
 *    (`/profile/42`, `?c=123`) never leaves the device, and the "Pages and
 *    screens" report stays low-cardinality.
 *  - No content, ever: message text, pay figures, names, notes are never event
 *    parameters. `trackEvent` params are for enums, counts and flags.
 *
 * RUNTIME CONTRACT:
 *  - Lazy. The SDK is a dynamic import — its own chunk, never on the boot path,
 *    and never fetched at all when analytics is off (`VITE_FIREBASE_ANALYTICS=off`).
 *  - Never throws, never blocks. Every public function is synchronous and
 *    queues (bounded) until the SDK is up; every SDK failure — unsupported
 *    browser, blocked storage, a broken config — quietly disables the module.
 *    Analytics can never take a screen down.
 *  - Offline-safe. `src/config/firebase.ts` carries the `measurementId`, so an
 *    offline start falls back to it instead of waiting on Firebase's dynamic
 *    config; gtag's own script simply fails to load and events are dropped.
 *  - Same code in the browser PWA and inside the Capacitor WebView (Android's
 *    `https://localhost` origin persists cookies, so the GA client id is
 *    stable across launches). The native-SDK upgrade path is in docs/analytics.md.
 *
 * Wiring: `initAnalytics(router)` from main.ts after mount; the session store
 * calls {@link identifyAnalyticsUser} / {@link clearAnalyticsUser}.
 */
import type { Analytics } from 'firebase/analytics';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { ANALYTICS_ENABLED, APP_VERSION, IS_DEV } from '@/utils/env';
import { FIREBASE_WEB_CONFIG } from '@/config/firebase';
import { getSessionEpoch, getSessionUserId, getStoredCompanyKey } from '@/services/api/erpat-client';
import { isStandalone } from '@/pwa/install';

/**
 * Event parameter values GA accepts. Keep them to enums, counts and flags —
 * never free text, never anything that identifies a person or a record.
 */
export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

export type AnalyticsStatus =
  /** Switched off for this build (`VITE_FIREBASE_ANALYTICS=off`). */
  | 'off'
  /** Enabled, `initAnalytics()` not called yet. */
  | 'idle'
  /** SDK chunk loading; calls are queued. */
  | 'loading'
  /** Live — calls go straight to the SDK. */
  | 'ready'
  /** The SDK could not start here (unsupported runtime, blocked storage, bad config). */
  | 'unavailable';

type AnalyticsSdk = typeof import('firebase/analytics');

interface Runtime {
  analytics: Analytics;
  sdk: AnalyticsSdk;
}

/** A unit of work against the SDK. May be async — the queue drains them IN ORDER. */
type Op = (rt: Runtime) => void | Promise<void>;

/**
 * Calls buffered before the SDK is up. Boot logs a handful (a screen view, an
 * identity); anything beyond this is a bug, not a backlog worth keeping.
 */
const MAX_PENDING = 32;

/** GA4 caps a user-property value at 36 characters (longer ones are dropped server-side). */
const USER_PROPERTY_MAX = 36;

let status: AnalyticsStatus = ANALYTICS_ENABLED ? 'idle' : 'off';
let runtime: Runtime | null = null;
let router: Router | null = null;
const queue: Op[] = [];
let draining = false;

/* ── Lifecycle ─────────────────────────────────────────────────────────── */

/** Where analytics stands in this runtime — for the About page and tests. */
export function analyticsStatus(): AnalyticsStatus {
  return status;
}

/**
 * Start analytics: hook screen views onto the router and load the SDK in the
 * background. Call ONCE, after `router.isReady()` and `app.mount()` — the
 * current route is logged as the first screen, so it must be the resolved one.
 * A no-op when analytics is off for this build.
 */
export function initAnalytics(appRouter: Router): void {
  if (status !== 'idle') return;
  router = appRouter;
  status = 'loading';

  // The route the app booted into. afterEach() never replays the initial
  // navigation, and this runs after router.isReady(), so it is already resolved.
  logRoute(appRouter.currentRoute.value);
  appRouter.afterEach((to, from, failure) => {
    // Aborted, redirected or duplicated navigation — the screen did not change.
    if (failure) return;
    // A query-only change (`?c=`, `?view=`, `?tab=`) is a mode of the same
    // screen, not a new one. Screens that want those counted report them as
    // explicit events.
    if (to.path === from.path) return;
    logRoute(to);
  });

  void load();
}

async function load(): Promise<void> {
  try {
    const [{ getApps, initializeApp, setLogLevel }, sdk] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);

    // No IndexedDB, cookies disabled, an extension context, … — the SDK would
    // throw from initializeAnalytics() on any of these.
    if (!(await sdk.isSupported())) {
      becomeUnavailable('unsupported runtime');
      return;
    }

    // Dev keeps the SDK's own warnings visible (a mismatched measurement id,
    // an unreachable config endpoint); a shipped build stays quiet — an offline
    // start would otherwise print a config-fetch warning on every launch.
    setLogLevel(IS_DEV ? 'warn' : 'silent');

    // HMR can re-run this module while the default app already exists.
    const app = getApps()[0] ?? initializeApp(FIREBASE_WEB_CONFIG);
    const analytics = sdk.initializeAnalytics(app, {
      config: {
        // Views are OUR screen_view events (one per route, pattern-scrubbed).
        // gtag's automatic page_view would count the boot URL a second time —
        // with its real location.
        send_page_view: false,
        // Show dev traffic in GA4 → Admin → DebugView as it happens. Dev-only,
        // and it must stay so: every build reports to the same stream, and the
        // property's developer-traffic filter drops debug_mode events from the
        // reports — a shipped build carrying the flag would vanish from them.
        ...(IS_DEV ? { debug_mode: true } : {}),
      },
    });
    sdk.setUserProperties(analytics, runtimeProperties());

    runtime = { analytics, sdk };
    status = 'ready';
    void drain();
  } catch (err) {
    becomeUnavailable('SDK failed to start', err);
  }
}

function becomeUnavailable(reason: string, err?: unknown): void {
  status = 'unavailable';
  queue.length = 0;
  if (IS_DEV) console.warn(`[analytics] disabled — ${reason}`, err ?? '');
}

/**
 * Run `op` against the SDK, or queue it until the SDK is up. Ops run strictly
 * in call order (the queue drains sequentially, awaiting async ones), which is
 * what lets an identity be set BEFORE the sign-in event that follows it.
 */
function run(op: Op): void {
  if (status === 'off' || status === 'unavailable') return;
  if (queue.length >= MAX_PENDING) queue.shift();
  queue.push(op);
  if (runtime) void drain();
}

async function drain(): Promise<void> {
  if (draining || !runtime) return;
  draining = true;
  try {
    for (let op = queue.shift(); op; op = queue.shift()) {
      try {
        await op(runtime);
      } catch (err) {
        // One bad call must not stall the ones behind it.
        if (IS_DEV) console.warn('[analytics] call failed', err);
      }
    }
  } finally {
    draining = false;
  }
}

/* ── Screens ───────────────────────────────────────────────────────────── */

type RouteLike = Pick<RouteLocationNormalizedLoaded, 'name' | 'path' | 'matched'>;

/**
 * The screen's stable name — the route NAME (`home`, `conversation`), which is
 * what the reports group by. Falls back to the pattern for an unnamed route.
 */
export function screenNameOf(route: RouteLike): string {
  return typeof route.name === 'string' && route.name !== '' ? route.name : screenClassOf(route);
}

/**
 * The screen's "class" — the matched route PATTERN (`/tabs/messages/:id`),
 * i.e. which page rendered. The closest web analogue of a native screen
 * class, and safe by construction: it is the template, never the concrete
 * path with a record id in it.
 */
export function screenClassOf(route: RouteLike): string {
  const record = route.matched[route.matched.length - 1];
  return record?.path || route.path;
}

function logRoute(route: RouteLocationNormalizedLoaded): void {
  trackScreenView(screenNameOf(route), screenClassOf(route));
}

/**
 * Report a screen that is not a route — a full-screen modal, a viewer, a
 * wizard step. Routes are reported automatically; do not call this for them.
 */
export function trackScreenView(name: string, screenClass: string = name): void {
  // Left un-annotated on purpose: the SDK's `screen_view` overload needs the
  // two `firebase_*` keys typed as plain strings, which `AnalyticsParams` widens.
  const params = {
    firebase_screen: name,
    firebase_screen_class: screenClass,
    ...locationOverride(),
  };
  run(({ analytics, sdk }) => sdk.logEvent(analytics, 'screen_view', params));
}

/**
 * `page_location` / `page_title` for every event we send, captured at CALL
 * time (a queued event must describe the screen it was raised on, not the one
 * showing when the SDK finally comes up). gtag would otherwise attach the live
 * `location.href`, ids and all. The pattern keeps the "Pages" reports
 * low-cardinality and record ids on the device; `document.title` is a constant
 * "Syntry", so the route name makes a far better page title.
 */
function locationOverride(): AnalyticsParams {
  const route = router?.currentRoute.value;
  if (!route) return {};
  const origin = typeof location !== 'undefined' ? location.origin : '';
  return {
    page_location: origin + screenClassOf(route),
    page_title: screenNameOf(route),
  };
}

/* ── Events ────────────────────────────────────────────────────────────── */

/**
 * Report an event. Use GA4's recommended names where one fits (`login`,
 * `share`, `search`, …) and snake_case custom names otherwise; params are
 * enums / counts / flags only (see the privacy contract above). Safe to call
 * any time, from anywhere — before the SDK is up it queues, when analytics is
 * off it is a no-op.
 */
export function trackEvent(name: string, params: AnalyticsParams = {}): void {
  const eventParams: AnalyticsParams = { ...compact(params), ...locationOverride() };
  // The explicit `<string>` picks the SDK's custom-event overload for a name
  // that is only known at runtime; inference alone collapses it to `never`.
  run(({ analytics, sdk }) => sdk.logEvent<string>(analytics, name, eventParams));
}

/* ── Identity ──────────────────────────────────────────────────────────── */

/**
 * Attach the signed-in user to subsequent events, under a pseudonym (see
 * {@link pseudonymousUserId}), plus the tenant slug as a user property so
 * reports can be cut per company. Reads the identity straight from the token
 * and the remembered company key, so it is correct the instant a token exists
 * — call it wherever a session starts (sign-in, MFA completion, restore).
 */
export function identifyAnalyticsUser(): void {
  const companyKey = getStoredCompanyKey() ?? '';
  const userId = getSessionUserId();
  if (!userId) return;
  const epoch = getSessionEpoch();

  run(async ({ analytics, sdk }) => {
    const id = await pseudonymousUserId(companyKey, userId);
    // A sign-out won the race while we hashed: never let an identity from the
    // previous session land on the next one's events (the same guard the
    // token refresh uses — see erpat-client.ts).
    if (epoch !== getSessionEpoch()) return;
    sdk.setUserId(analytics, id);
    sdk.setUserProperties(analytics, { company_key: clip(companyKey) || null });
  });
}

/** Detach the user: the sign-in screen and anything after it is anonymous again. */
export function clearAnalyticsUser(): void {
  run(({ analytics, sdk }) => {
    sdk.setUserId(analytics, null);
    sdk.setUserProperties(analytics, { company_key: null });
  });
}

/**
 * A stable pseudonym for GA's `user_id`: SHA-256 over
 * `syntry:<company>:<userId>`, hex, first 32 characters. One-way, so the
 * ERPat employee id itself never reaches Google, yet the same person hashes to
 * the same id on every device — which is all GA needs to count them once.
 * `null` when SubtleCrypto is unavailable (an `http://<lan-ip>` dev origin is
 * not a secure context): no identity is better than a guessable one.
 */
export async function pseudonymousUserId(companyKey: string, userId: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  try {
    const bytes = new TextEncoder().encode(`syntry:${companyKey}:${userId}`);
    const digest = new Uint8Array(await subtle.digest('SHA-256', bytes));
    return Array.from(digest.subarray(0, 16), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/* ── Helpers ───────────────────────────────────────────────────────────── */

/**
 * Build/runtime facts a web stream cannot see on its own, set once per boot
 * and identity-free: which platform (`web` / `android` / `ios`), whether the
 * app runs as a native shell, an installed PWA or a browser tab, and which
 * Syntry version — so a regression can be pinned to a build.
 */
function runtimeProperties(): Record<string, string> {
  const mode = Capacitor.isNativePlatform() ? 'native' : isStandalone() ? 'standalone' : 'browser';
  return {
    app_platform: clip(Capacitor.getPlatform()),
    app_mode: mode,
    app_version: clip(APP_VERSION),
  };
}

/** Trim a value to GA4's user-property limit. */
function clip(value: string): string {
  return value.length > USER_PROPERTY_MAX ? value.slice(0, USER_PROPERTY_MAX) : value;
}

/** Drop `undefined` params so gtag never serialises an empty key. */
function compact(params: AnalyticsParams): AnalyticsParams {
  const out: AnalyticsParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}
